import { Dropdown, Empty, Flex, Spin, Tooltip } from 'antd';
import {
  AddCell,
  AddColumnCell,
  BlankCell,
  Cell,
  Container,
  HeadCell,
  MoreIcon,
  SlashHeader,
  SpinContainer,
  StickyBlankCell,
  TableContainer,
  TableHeader,
  TableRow,
} from './style';
import { ActiveStatus, Cause, Frequency, Issue } from '../../types';
import { DownOutlined, MoreOutlined, PlusOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import SelectFrequency from './selectFrequency';
import { useAppDispatch } from '../../store';
import {
  fetchSolutionGrid,
  selectActiveSolutionGrids,
  selectCurrentSolutionGridVersions,
  selectIsFetchingSolutionGrid,
  selectSolutionGrids,
  setSolutionGrids,
  updateFrequency,
} from '../../slices/solutionGrids';
import { useSelector } from 'react-redux';
import { frequencies } from '../../constants';
import {
  fetchTemplateCauses,
  fetchTemplateIssues,
  selectDraftTemplate,
  selectTemplateCurrentCauses,
  selectTemplateCurrentIssues,
} from '../../slices/templates';
import SelectCauseDrawer from '../selectCauseOrIssueDrawer';
import { initSolutionGrid } from '../../utils/dataMapper';

export enum SolutionGridMode {
  READONLY = 'READONLY',
  UPDATE_RATIO = 'UPDATE_RATIO',
  EDIT = 'EDIT',
}

const renderTitle = (text: string) => {
  return text.length > 20 ? `${text.substring(0, 20)}...` : text;
};

const SolutionGrid = ({
  templateId,
  mode = SolutionGridMode.READONLY,
}: {
  templateId: string;
  mode?: SolutionGridMode;
}) => {
  const [isSelectFrequencyOpened, setIsSelectFrequencyOpened] = useState(false);
  const [isSelectCauseOrIssueDrawerOpened, setIsSelectCauseDrawerOpened] =
    useState(false);
  const [draftCauseIds, setDraftCauseIds] = useState<string[]>([]);
  const [draftIssueIds, setDraftIssueIds] = useState<string[]>([]);
  const dispatch = useAppDispatch();

  const addTypeRef = useRef<string>(null);

  const { templateID } = useSelector(selectDraftTemplate) || {};
  const issues = useSelector(selectTemplateCurrentIssues);
  const causes = useSelector(selectTemplateCurrentCauses);

  const currentSolutionGridVersions = useSelector(
    selectCurrentSolutionGridVersions
  );
  const solutionGrids = useSelector(selectSolutionGrids);
  const activeSolutionGrids = useSelector(selectActiveSolutionGrids);
  const isFetching = useSelector(selectIsFetchingSolutionGrid);

  const activeIssueIds = useMemo(
    () =>
      Array.from(new Set(activeSolutionGrids.map((i) => String(i.issueID)))),
    [activeSolutionGrids]
  );
  const activeCauseIds = useMemo(
    () =>
      Array.from(new Set(activeSolutionGrids.map((i) => String(i.causeID)))),
    [activeSolutionGrids]
  );
  const displayIssues = useMemo(
    () =>
      issues.filter(
        (i) =>
          activeIssueIds.includes(String(i.issueID)) ||
          draftIssueIds.includes(String(i.issueID))
      ),
    [activeIssueIds, draftIssueIds, issues]
  );

  const displayCauses = useMemo(
    () =>
      causes.filter(
        (i) =>
          activeCauseIds.includes(String(i.causeID)) ||
          draftCauseIds.includes(String(i.causeID))
      ),
    [activeCauseIds, causes, draftCauseIds]
  );

  const existingIssues = issues.filter((i) =>
    activeIssueIds.includes(String(i.issueID))
  );
  const existingCauses = causes.filter((i) =>
    activeCauseIds.includes(String(i.causeID))
  );

  const inactiveIssues =
    issues?.filter(
      (i) =>
        !activeIssueIds.includes(String(i.issueID)) &&
        !draftIssueIds.includes(String(i.issueID))
    ) || [];

  const inactiveCauses =
    causes?.filter(
      (i) =>
        !activeCauseIds.includes(String(i.causeID)) &&
        !draftCauseIds.includes(String(i.causeID))
    ) || [];

  const invalidSolutionGrid =
    displayIssues.length === 0 &&
    displayCauses.length === 0 &&
    (inactiveCauses.length === 0 || inactiveIssues.length === 0);

  const handleIssueRemove = (issueID: string) => {
    setDraftIssueIds((ids) => ids.filter((id) => id !== String(issueID)));
    const newSolutionGrids = solutionGrids.map((i) => ({
      ...i,
      activeStatus:
        String(i.issueID) === String(issueID)
          ? ActiveStatus.INACTIVE
          : i.activeStatus,
    }));
    dispatch(setSolutionGrids(newSolutionGrids));
  };

  const handleCauseRemove = (causeID: string) => {
    setDraftCauseIds((ids) => ids.filter((id) => id !== String(causeID)));
    const newSolutionGrids = solutionGrids.map((i) => ({
      ...i,
      activeStatus:
        String(i.causeID) === String(causeID)
          ? ActiveStatus.INACTIVE
          : i.activeStatus,
    }));
    dispatch(setSolutionGrids(newSolutionGrids));
  };

  const currentItemRef = useRef<{
    solutionGridID: string;
    relevanceRatio?: Frequency;
  }>({ solutionGridID: '' });

  const handleSelectFrequency = async (relevanceRatio: Frequency) => {
    setIsSelectFrequencyOpened(false);
    dispatch(
      updateFrequency({
        id: currentItemRef.current.solutionGridID,
        relevanceRatio,
      })
    );
  };

  const handleFrequencyClick = (currentItem: {
    solutionGridID: string;
    relevanceRatio?: Frequency;
  }) => {
    if (mode === SolutionGridMode.READONLY) {
      return;
    }
    currentItemRef.current = currentItem;
    setIsSelectFrequencyOpened(true);
  };

  const handleAddItems = async (selectedIds: string[]) => {
    setIsSelectCauseDrawerOpened(false);
    if (!selectedIds.length) {
      return;
    }

    const isCause = addTypeRef.current === 'cause';
    const nextDraftCauseIds = isCause
      ? Array.from(new Set([...draftCauseIds, ...selectedIds]))
      : draftCauseIds;
    const nextDraftIssueIds = isCause
      ? draftIssueIds
      : Array.from(new Set([...draftIssueIds, ...selectedIds]));
    const selectedCauses = causes.filter((i) =>
      selectedIds.includes(String(i.causeID))
    );
    const selectedIssues = issues.filter((i) =>
      selectedIds.includes(String(i.issueID))
    );
    const draftCauses = causes.filter((i) =>
      nextDraftCauseIds.includes(String(i.causeID))
    );
    const draftIssues = issues.filter((i) =>
      nextDraftIssueIds.includes(String(i.issueID))
    );
    const uniqueCauses = (items: Cause[]) =>
      items.filter(
        (item, index, list) =>
          list.findIndex((i) => String(i.causeID) === String(item.causeID)) ===
          index
      );
    const uniqueIssues = (items: Issue[]) =>
      items.filter(
        (item, index, list) =>
          list.findIndex((i) => String(i.issueID) === String(item.issueID)) ===
          index
      );
    const causesForGrid = uniqueCauses([
      ...existingCauses,
      ...draftCauses,
      ...(isCause ? selectedCauses : []),
    ]);
    const issuesForGrid = uniqueIssues([
      ...existingIssues,
      ...draftIssues,
      ...(isCause ? [] : selectedIssues),
    ]);

    if (!causesForGrid.length || !issuesForGrid.length) {
      setDraftCauseIds(nextDraftCauseIds);
      setDraftIssueIds(nextDraftIssueIds);
      return;
    }

    const { newSolutionGrid } = initSolutionGrid({
      issues: issuesForGrid,
      causes: causesForGrid,
      templateID: String(templateID || templateId),
      existingSolutionGrids: solutionGrids || [],
      versionID: currentSolutionGridVersions?.versionID,
    });
    const causeIdsForGrid = causesForGrid.map((i) => String(i.causeID));
    const issueIdsForGrid = issuesForGrid.map((i) => String(i.issueID));

    const nextSolutionGrid = solutionGrids
      .map((i) => ({
        ...i,
        ...(causeIdsForGrid.includes(String(i.causeID)) &&
        issueIdsForGrid.includes(String(i.issueID))
          ? { activeStatus: ActiveStatus.ACTIVE }
          : {}),
      }))
      .concat(newSolutionGrid);
    dispatch(setSolutionGrids(nextSolutionGrid));
    setDraftCauseIds([]);
    setDraftIssueIds([]);
  };

  const handleAddClick = (type: string) => {
    // if (inactiveCauses.length === 0 || inactiveIssues.length === 0) {
    //   messageApi.warning(
    //     'Please add at least 1 cause and 1 issue to continue!'
    //   );
    //   return;
    // }
    addTypeRef.current = type;
    setIsSelectCauseDrawerOpened(true);
  };

  useEffect(() => {
    if (!templateId) {
      return;
    }
    dispatch(fetchSolutionGrid({ templateID: templateId }));
  }, [templateId, dispatch]);

  useEffect(() => {
    if (!templateId) {
      return;
    }
    dispatch(fetchTemplateIssues({ templateId }));
    dispatch(fetchTemplateCauses({ templateId }));
  }, [templateId, dispatch]);

  return (
    <Container>
      {/* Header */}
      {isFetching && (
        <SpinContainer>
          <Spin fullscreen spinning={isFetching} />
        </SpinContainer>
      )}
      {invalidSolutionGrid ? (
        !isFetching && (
          <Empty
            style={{ margin: 20 }}
            description="Please create at least 1 cause and 1 issue to continue!"
          />
        )
      ) : (
        <TableContainer>
          <TableHeader>
            <SlashHeader>
              <svg width="100%" height="100%" viewBox="0 0 100 60">
                <line
                  x1="0"
                  y1="0"
                  x2="100"
                  y2="60"
                  stroke="#d9d9da"
                  strokeWidth="1"
                />
              </svg>
              <span className="text-left">
                Causes <span>({displayCauses.length})</span>
              </span>
              <span className="text-right">
                Issues <span>({displayIssues.length})</span>
              </span>
            </SlashHeader>
            {displayIssues.map((issue) => {
              return (
                <HeadCell key={`header_issue_${issue.issueID}`}>
                  <Flex flex={1}>
                    <Tooltip placement="top" title={issue.name}>
                      <span className="title">{renderTitle(issue.name)}</span>
                    </Tooltip>
                  </Flex>
                  {mode !== SolutionGridMode.READONLY &&
                    (displayIssues.length !== 1 ||
                      draftIssueIds.includes(String(issue.issueID))) && (
                      <Dropdown
                        menu={{
                          items: [
                            {
                              label: 'Remove',
                              key: 'remove-issue',
                            },
                          ],
                          onClick: () => handleIssueRemove(issue.issueID),
                        }}
                        placement="bottomRight"
                        arrow
                        trigger={['click']}
                      >
                        <MoreIcon>
                          <MoreOutlined />
                        </MoreIcon>
                      </Dropdown>
                    )}
                </HeadCell>
              );
            })}
            {mode !== SolutionGridMode.READONLY && (
              <AddColumnCell
                onClick={() => inactiveIssues.length && handleAddClick('issue')}
                className={inactiveIssues.length ? '' : 'disabled'}
              >
                <PlusOutlined /> Add Issue
              </AddColumnCell>
            )}
          </TableHeader>

          {displayCauses.map(({ causeID, name: causeName }) => (
            <Flex gap={1} key={`header-cause-${causeID}`}>
              <HeadCell>
                <Flex flex={1}>
                  <Tooltip placement="top" title={causeName}>
                    <span className="title">{renderTitle(causeName)}</span>
                  </Tooltip>
                </Flex>
                {mode !== SolutionGridMode.READONLY &&
                  (displayCauses.length !== 1 ||
                    draftCauseIds.includes(String(causeID))) && (
                    <Dropdown
                      menu={{
                        items: [
                          {
                            label: 'Remove',
                            key: 'remove-cause',
                          },
                        ],
                        onClick: () => handleCauseRemove(causeID),
                      }}
                      placement="bottomRight"
                      arrow
                      trigger={['click']}
                    >
                      <MoreIcon>
                        <MoreOutlined />
                      </MoreIcon>
                    </Dropdown>
                  )}
              </HeadCell>
              {displayIssues.map((issue) => {
                const grid = activeSolutionGrids.find(
                  (item) =>
                    String(item.causeID) === String(causeID) &&
                    String(item.issueID) === String(issue.issueID)
                );

                return grid ? (
                  <Cell
                    key={`grid_${grid.gridID}-issue_${grid.issueID}-cause_${grid.causeID}`}
                    data-id={grid.gridID}
                    className={
                      frequencies.find((f) => f.value === grid.relevanceRatio)
                        ?.label
                    }
                    onClick={() =>
                      handleFrequencyClick({
                        solutionGridID: grid.gridID,
                        relevanceRatio: grid.relevanceRatio,
                      })
                    }
                  >
                    {grid.relevanceRatio ? (
                      <span>
                        {
                          frequencies.find(
                            (f) => f.value === grid.relevanceRatio
                          )?.label
                        }
                      </span>
                    ) : (
                      <>
                        {mode === SolutionGridMode.READONLY ? (
                          <BlankCell />
                        ) : (
                          <>
                            Select Frequency <DownOutlined />
                          </>
                        )}
                      </>
                    )}
                  </Cell>
                ) : (
                  <Cell
                    key={`draft_grid_issue_${issue.issueID}-cause_${causeID}`}
                  >
                    <BlankCell />
                  </Cell>
                );
              })}
              {mode !== SolutionGridMode.READONLY && <StickyBlankCell />}
            </Flex>
          ))}
          {mode !== SolutionGridMode.READONLY && (
            <TableRow>
              <AddCell
                onClick={() => inactiveCauses.length && handleAddClick('cause')}
                className={inactiveCauses.length ? '' : 'disabled'}
              >
                <PlusOutlined /> Add Cause
              </AddCell>
            </TableRow>
          )}
        </TableContainer>
      )}

      <SelectFrequency
        open={isSelectFrequencyOpened}
        onClose={() => setIsSelectFrequencyOpened(false)}
        onConfirm={handleSelectFrequency}
        defaultFrequency={currentItemRef.current.relevanceRatio}
      />

      <SelectCauseDrawer
        title={`Add ${addTypeRef.current === 'cause' ? 'Causes' : 'Issues'} to the Solution Grid`}
        open={isSelectCauseOrIssueDrawerOpened}
        onClose={() => {
          setIsSelectCauseDrawerOpened(false);
        }}
        onConfirm={handleAddItems}
        data={addTypeRef.current === 'cause' ? inactiveCauses : inactiveIssues}
      ></SelectCauseDrawer>
    </Container>
  );
};

export default SolutionGrid;
